import React, { useState, useEffect } from 'react';
import './MarathonMode.css';

/**
 * Marathon Mode - Answer 50 questions, no time limit
 * Focus on endurance and comprehensive review
 */
const MarathonMode = ({ data }) => {
  const [gameState, setGameState] = useState('menu'); // menu, playing, paused, finished
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [pauseTime, setPauseTime] = useState(0);

  const TOTAL_QUESTIONS = 50;

  useEffect(() => {
    if (data && data.subjects) {
      loadQuestions();
    }
  }, [data]);

  const loadQuestions = () => {
    const allQuestions = data.subjects.flatMap(subject =>
      subject.topics.flatMap(topic =>
        topic.multiple_choice_questions.map(q => ({
          ...q,
          subject: subject.name,
          topic: topic.name
        }))
      )
    );

    // Shuffle and take 50
    const shuffled = allQuestions.sort(() => Math.random() - 0.5);
    setQuestions(shuffled.slice(0, TOTAL_QUESTIONS));
  };

  const startMarathon = () => {
    setGameState('playing');
    setCurrentIndex(0);
    setAnswers([]);
    setSelectedAnswer(null);
    setShowFeedback(false);
    setStartTime(Date.now());
    setPauseTime(0);
  };

  const pauseMarathon = () => {
    setGameState('paused');
    setPauseTime(Date.now());
  };

  const resumeMarathon = () => {
    setGameState('playing');
    const pauseDuration = Date.now() - pauseTime;
    setStartTime(startTime + pauseDuration);
  };

  const handleAnswer = (answer) => {
    if (showFeedback) return;

    setSelectedAnswer(answer);
    setShowFeedback(true);

    const isCorrect = answer === questions[currentIndex].answer;
    const newAnswers = [...answers];
    newAnswers[currentIndex] = {
      question: questions[currentIndex],
      selected: answer,
      correct: isCorrect,
      timestamp: Date.now()
    };
    setAnswers(newAnswers);
  };

  const nextQuestion = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedAnswer(null);
      setShowFeedback(false);
    } else {
      finishMarathon();
    }
  };

  const previousQuestion = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setSelectedAnswer(answers[currentIndex - 1]?.selected || null);
      setShowFeedback(!!answers[currentIndex - 1]);
    }
  };

  const finishMarathon = () => {
    setGameState('finished');
    
    // Save to history
    const history = JSON.parse(localStorage.getItem('marathonHistory') || '[]');
    const totalTime = Math.floor((Date.now() - startTime) / 1000);
    const correct = answers.filter(a => a.correct).length;
    
    history.push({
      date: new Date().toISOString(),
      totalQuestions: TOTAL_QUESTIONS,
      correct,
      accuracy: Math.round((correct / TOTAL_QUESTIONS) * 100),
      timeSeconds: totalTime,
      answers
    });
    
    localStorage.setItem('marathonHistory', JSON.stringify(history.slice(-10)));
  };

  const getProgress = () => {
    return Math.round(((currentIndex + 1) / TOTAL_QUESTIONS) * 100);
  };

  const getCorrectCount = () => {
    return answers.filter(a => a.correct).length;
  };

  const getTimeElapsed = () => {
    if (!startTime) return '0:00';
    const seconds = Math.floor((Date.now() - startTime) / 1000);
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Menu Screen
  if (gameState === 'menu') {
    const history = JSON.parse(localStorage.getItem('marathonHistory') || '[]');
    
    return (
      <div className="marathon-menu">
        <h1>🏃 Marathon Mode</h1>
        <p className="marathon-description">
          Answer {TOTAL_QUESTIONS} questions at your own pace.<br/>
          No time limit - focus on accuracy and endurance!
        </p>

        <div className="marathon-features">
          <div className="feature">
            <span className="feature-icon">📚</span>
            <span className="feature-text">{TOTAL_QUESTIONS} Questions</span>
          </div>
          <div className="feature">
            <span className="feature-icon">⏱️</span>
            <span className="feature-text">No Time Limit</span>
          </div>
          <div className="feature">
            <span className="feature-icon">💪</span>
            <span className="feature-text">Test Endurance</span>
          </div>
          <div className="feature">
            <span className="feature-icon">📊</span>
            <span className="feature-text">Detailed Review</span>
          </div>
        </div>

        <button className="start-marathon-btn" onClick={startMarathon}>
          Start Marathon
        </button>

        {history.length > 0 && (
          <div className="marathon-history">
            <h3>📜 Recent Marathons</h3>
            <div className="history-list">
              {history.slice(-5).reverse().map((run, idx) => (
                <div key={idx} className="history-item">
                  <div className="history-date">
                    {new Date(run.date).toLocaleDateString()}
                  </div>
                  <div className="history-stats">
                    <span className="history-score">{run.correct}/{run.totalQuestions}</span>
                    <span className="history-accuracy">{run.accuracy}%</span>
                    <span className="history-time">
                      {Math.floor(run.timeSeconds / 60)}:{(run.timeSeconds % 60).toString().padStart(2, '0')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Paused Screen
  if (gameState === 'paused') {
    return (
      <div className="marathon-paused">
        <h1>⏸️ Paused</h1>
        <div className="pause-stats">
          <div className="pause-stat">
            <span className="pause-label">Progress</span>
            <span className="pause-value">{currentIndex + 1}/{TOTAL_QUESTIONS}</span>
          </div>
          <div className="pause-stat">
            <span className="pause-label">Correct</span>
            <span className="pause-value">{getCorrectCount()}</span>
          </div>
        </div>
        <div className="pause-actions">
          <button className="resume-btn" onClick={resumeMarathon}>
            Resume Marathon
          </button>
          <button className="quit-btn" onClick={() => setGameState('menu')}>
            Quit
          </button>
        </div>
      </div>
    );
  }

  // Playing Screen
  if (gameState === 'playing' && questions.length > 0) {
    const currentQuestion = questions[currentIndex];
    const isCorrect = selectedAnswer === currentQuestion.answer;

    return (
      <div className="marathon-game">
        <div className="marathon-header">
          <div className="marathon-progress-bar">
            <div className="progress-fill" style={{ width: `${getProgress()}%` }} />
            <span className="progress-text">{currentIndex + 1} / {TOTAL_QUESTIONS}</span>
          </div>
          
          <div className="marathon-stats">
            <span className="stat">✓ {getCorrectCount()}</span>
            <span className="stat">⏱️ {getTimeElapsed()}</span>
            <button className="pause-btn" onClick={pauseMarathon}>⏸️</button>
          </div>
        </div>

        <div className="question-container">
          <div className="question-meta">
            <span className="subject-tag">{currentQuestion.subject}</span>
            <span className="topic-tag">{currentQuestion.topic}</span>
          </div>

          <h2 className="question-text">{currentQuestion.question}</h2>

          <div className="options-list">
            {currentQuestion.options.map((option, idx) => (
              <button
                key={idx}
                onClick={() => handleAnswer(option)}
                disabled={showFeedback}
                className={`marathon-option ${
                  showFeedback && option === currentQuestion.answer ? 'correct' : ''
                } ${
                  showFeedback && selectedAnswer === option && option !== currentQuestion.answer ? 'incorrect' : ''
                }`}
              >
                {option}
              </button>
            ))}
          </div>

          {showFeedback && (
            <div className={`feedback-box ${isCorrect ? 'feedback-correct' : 'feedback-incorrect'}`}>
              <h3>{isCorrect ? '✓ Correct!' : '✗ Incorrect'}</h3>
              {!isCorrect && (
                <p><strong>Correct answer:</strong> {currentQuestion.answer}</p>
              )}
              {currentQuestion.explanation && (
                <p className="explanation-text">{currentQuestion.explanation}</p>
              )}
            </div>
          )}
        </div>

        <div className="navigation-buttons">
          <button 
            className="nav-btn" 
            onClick={previousQuestion}
            disabled={currentIndex === 0}
          >
            ← Previous
          </button>
          
          {showFeedback && (
            <button className="nav-btn next-btn" onClick={nextQuestion}>
              {currentIndex < questions.length - 1 ? 'Next →' : 'Finish'}
            </button>
          )}
        </div>
      </div>
    );
  }

  // Finished Screen
  if (gameState === 'finished') {
    const correct = getCorrectCount();
    const accuracy = Math.round((correct / TOTAL_QUESTIONS) * 100);
    const totalTime = Math.floor((Date.now() - startTime) / 1000);
    const avgTime = Math.floor(totalTime / TOTAL_QUESTIONS);

    return (
      <div className="marathon-results">
        <h1>🏁 Marathon Complete!</h1>
        
        <div className="results-grid">
          <div className="result-card">
            <div className="result-value">{correct}/{TOTAL_QUESTIONS}</div>
            <div className="result-label">Correct</div>
          </div>
          <div className="result-card">
            <div className="result-value">{accuracy}%</div>
            <div className="result-label">Accuracy</div>
          </div>
          <div className="result-card">
            <div className="result-value">
              {Math.floor(totalTime / 60)}:{(totalTime % 60).toString().padStart(2, '0')}
            </div>
            <div className="result-label">Total Time</div>
          </div>
          <div className="result-card">
            <div className="result-value">{avgTime}s</div>
            <div className="result-label">Avg per Q</div>
          </div>
        </div>

        <div className="performance-rating">
          {accuracy >= 90 && <p>🏆 Outstanding! MCAT Ready!</p>}
          {accuracy >= 80 && accuracy < 90 && <p>⭐ Excellent Performance!</p>}
          {accuracy >= 70 && accuracy < 80 && <p>👍 Good Work!</p>}
          {accuracy < 70 && <p>💪 Keep Practicing!</p>}
        </div>

        <div className="results-actions">
          <button className="action-btn primary" onClick={startMarathon}>
            Run Another Marathon
          </button>
          <button className="action-btn secondary" onClick={() => setGameState('menu')}>
            Back to Menu
          </button>
        </div>
      </div>
    );
  }

  return <div>Loading...</div>;
};

export default MarathonMode;
